import { Task, Timestamp, WeekDays } from '@griply/types';
import {
  differenceInDays,
  endOfDay,
  format,
  isAfter,
  isBefore,
  isSameDay,
} from 'date-fns';

import { getActiveSchedule } from './get-active-schedule';
import { getScheduleDueDate } from './schedules';

export type GetTaskDueDateOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
};

export const getTaskDueDate = (
  task: Task,
  { today, weekStartsOn }: GetTaskDueDateOptions,
) => {
  const activeSchedule = getActiveSchedule(task.schedules, today);
  return activeSchedule
    ? getScheduleDueDate(activeSchedule, { today, weekStartsOn })
    : task.endStrategy?.deadline ?? undefined;
};

/** Generic translation function type compatible with i18next TFunction. */
export type TranslateFunction = (
  key: string,
  options?: Record<string, unknown>,
) => string;

export type GetDueLabelOptions = {
  t: TranslateFunction;
  today: Timestamp;
  hideFutureDue?: boolean;
  hideTodayDue?: boolean;
  hidePastDue?: boolean;
};

export const getDueLabel = (
  date: Timestamp | undefined,
  { t, today, hideTodayDue, hidePastDue, hideFutureDue }: GetDueLabelOptions,
) => {
  if (!date) {
    return;
  }
  if (hidePastDue && isBefore(date, today)) {
    return;
  }

  if (hideTodayDue && isSameDay(date, today)) {
    return;
  }

  if (hideFutureDue && isAfter(date, today)) {
    return;
  }

  if (isSameDay(date, today)) {
    return t('general.dates.today');
  }

  if (isBefore(date, today)) {
    return t('general.days-ago', {
      count: differenceInDays(endOfDay(today), endOfDay(date)),
    });
  }

  return format(date, 'd MMM');
};
